<script setup>
import {computed, onBeforeMount, useTemplateRef, shallowRef} from 'vue';
import Brand from "./components/Brand.vue";
import pluralize from "../utilities/pluralize";
import { useScroll } from '@vueuse/core'
import { useFuse } from '@vueuse/integrations/useFuse'

const props = defineProps( ['featuredBrandsJson', 'allBrandsJson'] );

let featuredBrands = shallowRef( [] );
let allBrands = shallowRef( [] );
let searchBrands = shallowRef( [] );
const sectionAlphabetisedRef = useTemplateRef( 'section-alphabetised' );

/**
 * Letter navigation
 */
const letterNavListRef = useTemplateRef( 'letter-nav-list' );
const {arrivedState: letterNavArrivedState, x: letterNavOffset} = useScroll( letterNavListRef, {
    behavior: 'smooth',
} );

/**
 * Navigate the letter navigation in either `left` or `right` direction.
 * 
 * @param {String} direction
 */
function moveLetterNav( direction ) {
    const multiplier = 'left' === direction ? -1 : 1;
    const width = letterNavListRef.value.getBoundingClientRect().width;
    
    if( width ) {
        letterNavOffset.value += width * 0.75 * multiplier; 
    }
}

/**
 * Parse the incoming JSON strings before the component mounts
 */
onBeforeMount( () => {
    featuredBrands.value = JSON.parse( props.featuredBrandsJson );
    allBrands.value = JSON.parse( props.allBrandsJson );
    searchBrands.value = Object.values( allBrands.value ).flat();
} )

/**
 * Search query and results
 */
let rawSearchQuery = shallowRef( '' );
let searchQuery = computed( () => rawSearchQuery.value.trim().replace( /[^A-Za-z0-9 ]/, '' ) );
let isSearch = computed( () => searchQuery.value.length > 0 );
const {results: searchResults} = useFuse( searchQuery, searchBrands, {
    fuseOptions: {
        keys: ['searchValue'],
        threshold: 0.3,
        ignoreDiacritics: true,
    }
} );

/**
 * Alphabet constant.
 */
const letters = [
    { id: "a", label: "A", text: "letter A" },
    { id: "b", label: "B", text: "letter B" },
    { id: "c", label: "C", text: "letter C" },
    { id: "d", label: "D", text: "letter D" },
    { id: "e", label: "E", text: "letter E" },
    { id: "f", label: "F", text: "letter F" },
    { id: "g", label: "G", text: "letter G" },
    { id: "h", label: "H", text: "letter H" },
    { id: "i", label: "I", text: "letter I" },
    { id: "j", label: "J", text: "letter J" },
    { id: "k", label: "K", text: "letter K" },
    { id: "l", label: "L", text: "letter L" },
    { id: "m", label: "M", text: "letter M" },
    { id: "n", label: "N", text: "letter N" },
    { id: "o", label: "O", text: "letter O" },
    { id: "p", label: "P", text: "letter P" },
    { id: "q", label: "Q", text: "letter Q" },
    { id: "r", label: "R", text: "letter R" },
    { id: "s", label: "S", text: "letter S" },
    { id: "t", label: "T", text: "letter T" },
    { id: "u", label: "U", text: "letter U" },
    { id: "v", label: "V", text: "letter V" },
    { id: "w", label: "W", text: "letter W" },
    { id: "x", label: "X", text: "letter X" },
    { id: "y", label: "Y", text: "letter Y" },
    { id: "z", label: "Z", text: "letter Z" },
    { id: "other", label: "#", text: "letter other brands" },
];

/**
 * If current device supports touch.
 */
const isTouchscreen = (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0));

</script>

<template>
    <!-- Featured brands section -->
    <section v-if="featuredBrands" class="section section--featured">
        <h3 class="section-title">Most popular brands</h3>
        
        <ul class="brand-grid">
            <Brand v-for="brand in featuredBrands" :key="brand.id" :id="brand.id" :link="brand.link" :logo="brand.logo" :name="brand.name" :count="brand.count" />
        </ul>
    </section>
    
    <section class="section section--navigation">
        <h3 class="section-title">All brands</h3>

        <div class="navigation">
            <!-- Search field -->
            <div class="search">
                <div class="search-field">
                    <label for="search-query" class="screen-reader-text">Type to search brands</label>
                    <input v-model="rawSearchQuery" type="search" placeholder="Type to search brands…" class="search-input" id="search-query" />

                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M17.4786265,15.4297733 C18.8960265,13.4955733 19.5309265,11.0974733 19.2561265,8.71531334 C18.9814265,6.33313334 17.8173265,4.14252334 15.9968265,2.58175334 C14.1763265,1.02098734 11.8336265,0.205158544 9.43744647,0.297488204 C7.04124647,0.389817344 4.76827647,1.38349334 3.07325647,3.07972334 C1.37824647,4.77595334 0.386190168,7.04964334 0.295575168,9.44590334 C0.204960168,11.8421733 1.02246547,14.1842733 2.58453647,16.0036733 C4.14660647,17.8230733 6.33804647,18.9855733 8.72042647,19.2585733 C11.1028265,19.5316733 13.5004265,18.8950733 15.4336265,17.4762733 L15.4321265,17.4762733 C15.4751265,17.5347733 15.5229265,17.5908733 15.5756265,17.6445733 L21.2114265,23.2803733 C21.4859265,23.5550733 21.8582265,23.7095735 22.2466265,23.7095735 C22.6349265,23.7095735 23.0073265,23.5555733 23.2820265,23.2811733 C23.5567265,23.0066733 23.7112267,22.6342733 23.7112267,22.2459733 C23.7112267,21.8576733 23.5572265,21.4851733 23.2828265,21.2104733 L17.6469265,15.5746733 C17.5946265,15.5216733 17.5383265,15.4741733 17.4786265,15.4297733 Z M17.8563265,9.80274334 C17.8563265,10.8600733 17.6480265,11.9069733 17.2434265,12.8837733 C16.8388265,13.8605733 16.2457265,14.7481733 15.4981265,15.4957733 C14.7505265,16.2433733 13.8630265,16.8364733 12.8861265,17.2410733 C11.9093265,17.6456733 10.8624265,17.8538733 9.80511647,17.8538733 C8.74781647,17.8538733 7.70087647,17.6456733 6.72406647,17.2410733 C5.74724647,16.8364733 4.85969647,16.2433733 4.11207647,15.4957733 C3.36444647,14.7481733 2.77140647,13.8605733 2.36679647,12.8837733 C1.96218647,11.9069733 1.75393647,10.8600733 1.75393647,9.80274334 C1.75393647,7.66743334 2.60218647,5.61958334 4.11207647,4.10970334 C5.62195647,2.59981334 7.66980647,1.75156334 9.80511647,1.75156334 C11.9404265,1.75156334 13.9882265,2.59981334 15.4981265,4.10970334 C17.0080265,5.61958334 17.8563265,7.66743334 17.8563265,9.80274334 Z"/></svg>
                </div>
            </div>

            <!-- Alphabetical navigation -->
            <div :class="`letter-navigation ${!isTouchscreen ? 'has-scroll-buttons' : ''}`" v-show="!isSearch">
                <button class="letter scroll-button scroll-button--left" v-if="!isTouchscreen" @click.prevent="moveLetterNav( 'left' )" :disabled="letterNavArrivedState.left" aria-hidden="true" tabindex="-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><polyline fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" points="15.25 4.714 8 11.286 .75 4.714" transform="rotate(90 8 8)"/></svg>
                </button>

                <nav :class="`container ${letterNavArrivedState.left ? 'first-letter-visible' : ''} ${letterNavArrivedState.right ? 'last-letter-visible' : ''}`">
                    <ul class="list" ref="letter-nav-list">
                        <li v-for="letter in letters" :key="letter.id">
                            <a v-if="allBrands[letter.id].length > 0" class="letter is-active" :href="`#${letter.id}`" :aria-label="`Skip to ${letter.text}`">
                                <span v-html="letter.label"></span>
                            </a>

                            <span v-else class="letter" :aria-label="`No brands for ${letter.text}`">
                            <span v-html="letter.label"></span>
                        </span>
                        </li>
                    </ul>
                </nav>

                <button class="letter scroll-button scroll-button--right" v-if="!isTouchscreen" @click.prevent="moveLetterNav( 'right' )" :disabled="letterNavArrivedState.right" aria-hidden="true" tabindex="-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><polyline fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" points="15.25 4.714 8 11.286 .75 4.714" transform="rotate(-90 8 8)"/></svg>
                </button>
            </div>
        </div>
    </section>
    
    <!-- Alphabetised brand list -->
    <section v-if="allBrands" v-show="!isSearch" class="section section--alphabetised" ref="section-alphabetised">
        <template v-for="letter in letters">
            <div v-if="allBrands[letter.id].length > 0" :key="letter.id" :id="letter.id" class="letter">
                <div class="letter-heading">
                    <h4 class="title" :aria-label="`Brands beginning with letter ${letter.text}`" v-html="letter.label"></h4>
                    
                    <div class="count" v-html="pluralize( allBrands[letter.id].length, '%s brand', '%s brands' )"></div>
                </div>
                
                <ul class="brand-grid">
                    <Brand v-for="brand in allBrands[letter.id]" :key="brand.id" :id="brand.id" :link="brand.link" :logo="brand.logo" :name="brand.name" :count="brand.count" />
                </ul>
            </div>
        </template>
    </section>
    
    <!-- Non-alphabetised brand list for search results -->
    <section v-show="isSearch" class="section section--results">
        <ul class="brand-grid" v-if="searchResults.length > 0">
            <Brand v-for="brand in searchResults" :key="brand.item.id" :id="brand.item.id" :link="brand.item.link" :logo="brand.item.logo" :name="brand.item.name" :count="brand.item.count" />
        </ul>
        
        <p class="no-search-results" v-if="0 === searchResults.length">No brands found.</p>
    </section>
</template>
